import { useState } from 'react';
import {
  BODY_IMAGES,
  getAvailableRegions,
  getPendingRegions,
  getPlacements,
  getRegion,
  getRegionGrid,
  getTotalPoints
} from '../../lib/domain-data';
import { AgeBand, BodySide, InjectionPoint, InjectionRegion } from '../../types/domain';

interface InjectionSitePickerProps {
  band: AgeBand;
  value: InjectionPoint | null;
  onChange: (point: InjectionPoint) => void;
  /** The rotation suggestion — highlighted, never auto-selected. */
  suggested?: InjectionPoint | null;
  /** Points used recently, so the patient can see what to rotate away from. */
  recent?: InjectionPoint[];
  /** Points already flagged for lipodystrophy (FR-11) — shown so they can be avoided. */
  lipoPoints?: InjectionPoint[];
}

const samePoint = (a: InjectionPoint | null | undefined, b: InjectionPoint) =>
  !!a && a.regionId === b.regionId && a.row === b.row && a.col === b.col;

/**
 * FR-09: pick a region on the body, zoom into it, then pick a point.
 *
 * The two-step flow is forced by the data model rather than chosen for looks —
 * a 7–18 patient has 104 selectable points, which cannot be tapped accurately
 * on one whole-body image.
 */
export const InjectionSitePicker: React.FC<InjectionSitePickerProps> = ({
  band,
  value,
  onChange,
  suggested,
  recent = [],
  lipoPoints = []
}) => {
  const [side, setSide] = useState<BodySide>('front');
  const [zoomRegion, setZoomRegion] = useState<InjectionRegion | null>(null);

  const pending = getPendingRegions(band);
  const availableIds = new Set(getAvailableRegions(band).map(r => r.id));

  // Placements are per band AND per view: the artwork's proportions differ too
  // much between an infant and a teenager to share one set of coordinates.
  const placements = getPlacements(band, side);
  const selectedRegion = getRegion(value?.regionId ?? null);

  if (zoomRegion) {
    const grid = getRegionGrid(zoomRegion.id, band);
    if (!grid) {
      setZoomRegion(null);
      return null;
    }
    const rows = Array.from({ length: grid.rows }, (_, i) => i + 1);
    const cols = Array.from({ length: grid.cols }, (_, i) => i + 1);

    return (
      <div className="site-picker">
        <div className="site-picker-head">
          <button type="button" className="site-back" onClick={() => setZoomRegion(null)}>
            ← Kembali ke gambar tubuh
          </button>
          <span className="site-picker-title">{zoomRegion.label}</span>
          <span className="site-picker-note">
            {grid.rows * grid.cols} titik ({grid.rows}×{grid.cols})
          </span>
        </div>

        <div className="site-zoom">
          {zoomRegion.zoomImage ? (
            <img
              className={`site-zoom-image${zoomRegion.mirrored ? ' site-zoom-image--mirrored' : ''}`}
              src={zoomRegion.zoomImage}
              alt={`Gambar ${zoomRegion.label}`}
            />
          ) : null}
          <div
            className="site-grid"
            style={{
              gridTemplateColumns: `repeat(${grid.cols}, 1fr)`,
              gridTemplateRows: `repeat(${grid.rows}, 1fr)`
            }}
          >
            {rows.map(row =>
              cols.map(col => {
                const point: InjectionPoint = { regionId: zoomRegion.id, row, col };
                const isSelected = samePoint(value, point);
                const isSuggested = samePoint(suggested, point);
                const isRecent = recent.some(r => samePoint(r, point));
                const isLipo = lipoPoints.some(l => samePoint(l, point));
                return (
                  <button
                    key={`${row}-${col}`}
                    type="button"
                    className={[
                      'site-point',
                      isSelected ? 'site-point--selected' : '',
                      isSuggested ? 'site-point--suggested' : '',
                      isRecent ? 'site-point--recent' : '',
                      isLipo ? 'site-point--lipo' : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-pressed={isSelected}
                    aria-label={
                      `${zoomRegion.label}, baris ${row}, kolom ${col}` +
                      (isSuggested ? ' (disarankan)' : '') +
                      (isRecent ? ' (baru dipakai)' : '') +
                      (isLipo ? ' (pernah ditandai lipodistrofi)' : '')
                    }
                    onClick={() => onChange(point)}
                  />
                );
              })
            )}
          </div>
        </div>

        <div className="site-legend">
          <span className="site-legend-item">
            <span className="site-point site-point--suggested site-legend-dot" /> Disarankan
          </span>
          <span className="site-legend-item">
            <span className="site-point site-point--recent site-legend-dot" /> Baru dipakai
          </span>
          <span className="site-legend-item">
            <span className="site-point site-point--selected site-legend-dot" /> Dipilih
          </span>
          {lipoPoints.length > 0 ? (
            <span className="site-legend-item">
              <span className="site-point site-point--lipo site-legend-dot" /> Lipodistrofi
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="site-picker">
      <div className="site-picker-head">
        <div className="site-side-toggle" role="group" aria-label="Tampilan tubuh">
          <button
            type="button"
            className={`site-side${side === 'front' ? ' site-side--active' : ''}`}
            aria-pressed={side === 'front'}
            onClick={() => setSide('front')}
          >
            Depan
          </button>
          <button
            type="button"
            className={`site-side${side === 'back' ? ' site-side--active' : ''}`}
            aria-pressed={side === 'back'}
            onClick={() => setSide('back')}
          >
            Belakang
          </button>
        </div>
        <span className="site-picker-note">{getTotalPoints(band)} titik tersedia</span>
      </div>

      <div className="site-body">
        <img
          className="site-body-image"
          src={BODY_IMAGES[band][side]}
          alt={`Gambar tubuh tampak ${side === 'front' ? 'depan' : 'belakang'}`}
        />
        {placements.map(placement => {
          const region = getRegion(placement.regionId);
          if (!region) {
            return null;
          }
          const usable = availableIds.has(region.id);
          const isChosen = value?.regionId === region.id;
          return (
            <button
              key={region.id}
              type="button"
              className={[
                'site-region',
                usable ? '' : 'site-region--pending',
                isChosen ? 'site-region--chosen' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                left: `${placement.x}%`,
                top: `${placement.y}%`,
                width: `${placement.width}%`,
                height: `${placement.height}%`
              }}
              disabled={!usable}
              aria-label={
                usable
                  ? `Pilih ${region.label}`
                  : `${region.label} — belum tersedia`
              }
              title={
                usable ? region.label : `${region.label} — titik suntik belum tersedia`
              }
              onClick={() => setZoomRegion(region)}
            >
              <span className="site-region-label">{region.label}</span>
            </button>
          );
        })}
      </div>

      {selectedRegion && value ? (
        <div className="site-chosen">
          Dipilih: <strong>{selectedRegion.label}</strong> · baris {value.row}, kolom{' '}
          {value.col}
        </div>
      ) : (
        <div className="site-chosen site-chosen--empty">
          Ketuk area hijau untuk memperbesar, lalu pilih titik suntik.
        </div>
      )}

      {pending.length > 0 ? (
        <div className="site-pending-note">
          {pending.map(r => r.label).join(', ')} belum bisa dipilih — daftar titik
          suntiknya belum tersedia.
        </div>
      ) : null}
    </div>
  );
};

export default InjectionSitePicker;
